<!-- Spacing to replace removed heading -->
<div class="mt-10"></div>

<!-- Product Info (Full Width) -->
<div class="-mx-2.5 mt-5">
  <div class="w-full px-2.5">
    <div class="mb-4">
      <div class="mb-3">
        <div class="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
          {{ productItem.product.name }}
        </div>
        @if (productItem.product.price !== null && productItem.product.price !== undefined) {
          <div class="text-base text-gray-600 dark:text-gray-400">
            Base Price: <span class="font-medium text-gray-900 dark:text-white">{{ productItem.product.price | currency:'GBP':'symbol':'1.2-2':'en-GB' }}</span>
          </div>
        }
      </div>
      @if (productItem.is_product_using_daily_price) {
        <div class="flex items-center gap-1.5 text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/20 rounded text-blue-600 dark:text-blue-400 w-fit">
          <lucide-icon [size]="12" [name]="Calendar"></lucide-icon>
          <span class="font-medium">This product is using daily pricing for this event date</span>
        </div>
      } @else if (productItem.is_discontinued && !isVenueHireProduct()) {
        <div class="flex items-center gap-1.5 text-xs px-2 py-1 bg-orange-50 dark:bg-orange-900/20 rounded text-orange-600 dark:text-orange-400 w-fit">
          <lucide-icon [size]="12" [name]="Calendar"></lucide-icon>
          <span class="font-medium">Discontinued</span>
        </div>
      }
    </div>
  </div>
</div>

<!-- Form Fields (Single Column) -->
@if (showTimelineSelector && timelineItemChoices.length > 1) {
  <div class="-mx-2.5 mt-5">
    <div class="w-full px-2.5">
      <app-select
        [height]="'11'"
        [control]="form.controls.event_timeline_item_id"
        [choices]="timelineItemChoices"
        [label]="'Itinerary Item'"
        [placeholder]="'No itinerary item'"
        [clearable]="true">
      </app-select>
    </div>
  </div>
}

@if (shouldShowPricingSection) {
  <div class="-mx-2.5 mt-5 flex flex-wrap gap-y-5">
    <!-- Quantity for non-variant-based products -->
    @if (!isVariantBased) {
      <div class="w-full px-2.5">
        <app-text-input
          [control]="form.controls.quantity"
          [name]="'qty'"
          [placeholder]="'Qty'"
          type="number"
          label="Quantity">
        </app-text-input>
      </div>
    }

    <!-- Manual amount for products without price -->
    @if (!productItem.product.price) {
      <div class="w-full px-2.5">
        <app-text-input
          [mask]="'separator.2'"
          [thousandSeparator]="','"
          [allowNegativeNumbers]="true"
          [control]="form.controls.amount"
          [name]="'amount'"
          [placeholder]="'0.00'"
          label="Amount"
          prefix="£">
        </app-text-input>
      </div>
    }

    <!-- VG-396: Multi-variant selections grouped by variant -->
    @if (hasVariants) {
      <div class="w-full px-2.5">
        <!-- Spacing to replace removed heading -->
        <div class="mb-4"></div>

        @for (entry of getVariantSelectionsByVariant() | keyvalue; track entry.key; let isLast = $last) {
          @let variantData = entry.value;
          @let variant = variantData.variant;
          <div class="mb-8">
            <!-- Variant header -->
            <div class="flex items-center justify-between mb-3">
              <h5 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
                <app-variant-pricing-label
                  [variantId]="variant.id!"
                  [variantName]="variant.name"
                  [product]="productItem.product"
                  [variantSelectionsFormArray]="form.controls.variant_selections"
                  [variantSelections]="productItem.variant_selections">
                </app-variant-pricing-label>
              </h5>
            </div>

            @if (productItem.product.is_variant_based && totalVariantQuantity > 0) {
              <div class="flex justify-end text-sm text-gray-600 dark:text-gray-300 mb-2">
                Total quantity: {{ totalVariantQuantity }}
              </div>
            }

            <!-- Check if variant has no options (accommodation case) -->
            @if (hasNoOptions(variant)) {
              <!-- Simplified UI for 0-option variants: only show notes -->
              @for (selectionIndex of variantData.indices; track trackByUniqueId($index, selectionIndex)) {
                @let selectionControl = form.controls.variant_selections.at(selectionIndex);
                <div class="mb-3">
                  <app-textarea
                    [control]="getNotesControl(selectionControl)"
                    label="Notes (optional)"
                    [rows]="2">
                  </app-textarea>
                </div>
              }
            } @else {
              <!-- Full UI for variants with options -->
              <!-- Existing selections for this variant -->
              @for (selectionIndex of variantData.indices; track trackByUniqueId($index, selectionIndex)) {
                @let selectionControl = form.controls.variant_selections.at(selectionIndex);
                <div class="border rounded-lg p-4 mb-3 bg-gray-50 dark:bg-gray-800">
                  <div class="space-y-3">
                    <!-- First row: Option select, Quantity, and Remove button -->
                    <div class="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                      <!-- Option select -->
                      <div class="md:col-span-7">
                        <app-select
                          [height]="'11'"
                          [control]="getVariantOptionControl(selectionControl)"
                          [choices]="getVariantOptionsChoices(variant.id!)"
                          placeholder="Select option..."
                          label="Option"
                          [allowWrap]="true">
                        </app-select>
                        @let info = getOptionInfo(selectionControl);
                        @if (info) {
                          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ info }}</p>
                        }
                      </div>

                      <!-- Quantity -->
                      <div class="md:col-span-3">
                        <app-text-input
                          [control]="getQuantityControl(selectionControl)"
                          label="Quantity"
                          type="number"
                          placeholder="Qty"
                          [max]="getSelectionMax(selectionControl)"
                          [helpText]="getSelectionCapHelp(selectionControl)"
                          [name]="'quantity_' + selectionIndex">
                        </app-text-input>
                      </div>

                      <!-- Remove button -->
                      <div class="md:col-span-2 items-center flex mt-6">
                        <button
                          type="button"
                          (click)="removeVariantOption(selectionIndex)"
                          class="w-full h-11 flex items-center justify-center px-3 py-2 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                          <lucide-icon [size]="16" [name]="Trash2"></lucide-icon>
                        </button>
                      </div>
                    </div>

                    <!-- Second row: Notes -->
                    <div>
                      <app-textarea
                        [control]="getNotesControl(selectionControl)"
                        label="Notes"
                        [rows]="2">
                      </app-textarea>
                    </div>

                    <!-- Daily pricing / Discontinued indicators -->
                    @let selectedOptionId = getVariantOptionControl(selectionControl).value;
                    @let selectedOption = getSelectedOption(variant.id!, selectedOptionId);
                    @if (selectedOption) {
                      @if (selectedOption.is_using_daily_price) {
                        <div class="flex items-center gap-1.5 text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/20 rounded text-blue-600 dark:text-blue-400 w-fit">
                          <lucide-icon [size]="12" [name]="Calendar"></lucide-icon>
                          <span class="font-medium">This option is using daily pricing for this event date</span>
                        </div>
                      } @else if (isOptionDiscontinued(selectedOption)) {
                        <div class="flex items-center gap-1.5 text-xs px-2 py-1 bg-orange-50 dark:bg-orange-900/20 rounded text-orange-600 dark:text-orange-400 w-fit">
                          <lucide-icon [size]="12" [name]="Calendar"></lucide-icon>
                          <span class="font-medium">Discontinued</span>
                        </div>
                      }
                    }
                  </div>
                </div>
              }

              <!-- Add button for this variant (hide only if variant has exactly 1 option) -->
              @if (variant.options && variant.options.length !== 1) {
                <button
                  type="button"
                  (click)="addVariantOption(variant)"
                  class="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors flex items-center justify-center gap-2">
                  <lucide-icon [size]="16" [name]="Plus"></lucide-icon>
                  <span>Add {{ variant.name }}</span>
                </button>
              }
            }

            <!-- Elegant separator (except for last variant) -->
            @if (!isLast) {
              <div class="mt-8 flex items-center justify-center">
                <div class="w-32 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent"></div>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
}

<!-- Pricing Panel (sticky at bottom) -->
@if (shouldShowPricingSection) {
  <app-product-item-pricing
    [product]="productItem.product"
    [eventId]="event.id"
    [variantSelectionsFormArray]="form.controls.variant_selections"
    [quantityControl]="form.controls.quantity"
    [amountControl]="form.controls.amount">
  </app-product-item-pricing>
}

<!-- Payment Status -->
<!--  VG-281: TEMPORARILY HIDDEN - Paid status feature (2025-09-30)-->
<!--  To revert: Uncomment lines 94-105 to show "Mark as Paid" checkbox-->
<!--  <div class="w-full px-2.5 mt-4" [formGroup]="form">-->
<!--    <div class="flex items-center gap-2">-->
<!--      <input-->
<!--        type="checkbox"-->
<!--        formControlName="is_paid"-->
<!--        id="is_paid"-->
<!--        class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700">-->
<!--      <label for="is_paid" class="text-sm font-medium text-gray-700 dark:text-gray-300">-->
<!--        Mark as Paid-->
<!--      </label>-->
<!--    </div>-->
<!--  </div>-->
<!--  END VG-281-->

<!-- Action Buttons -->
<div class="flex flex-row w-full justify-between mt-10">
  <div class="px-2.5">
    <app-button [label]="'Cancel'" [style]="'secondary'" (click)="cancel()"></app-button>
  </div>
  <div class="px-2.5">
    <app-button
      [label]="processing ? 'Updating...' : 'Update Product'"
      [disabled]="!form.valid || form.pristine || processing"
      (click)="save()">
    </app-button>
  </div>
</div>
